/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { fromJS, List } from 'immutable';
import { createSelector } from 'reselect';

import { personInitialState } from './reducers';
import { ActionResult } from 'common/constants';
import {
  IFallthroughCaptureImt,
  ILeadCallHistoryDtoImt,
  IPaymentAccountImt,
  IPersonDocumentsImt,
  IReminderDtoImt,
  ISMSImt,
  ReminderHistoryDtoImt,
} from 'common/components/PersonProfile/interfaces';
import { ILeadEmailHistoryDtoImt } from 'modules/crm/interfaces/leads';
import { IStoredFileSimpleDtoImt } from 'common/interfaces/uploadFile';
import { AppStoreTypeImt } from 'store';
import { IPageMetaImt } from 'common/interfaces/pagination';

const selectPersonPanelDomain = (state, { personId }: { personId: number }) => {
  return state.getIn(['persons', 'profiles', Number(personId)]);
};

export const createPersonSelectorFactory = (selectors: string | string[]) => () =>
  createSelector([selectPersonPanelDomain], state => {
    const st = state || fromJS(personInitialState);
    if (Array.isArray(selectors)) {
      return st.getIn(selectors);
    }
    return st.get(selectors);
  });

export const personsListPath = ['persons', 'profiles'];

export const selectPersonProfile = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'profile']);

export const selectPersonAdditionalFields = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'additionalFields']);

export const selectPersonProfileLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'profile', 'profileLoading']);

export const selectPersonUpdateProfileActionLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'updateProfileAction', 'isLoading']);
export const selectPersonUpdateProfileActionResult = (personId: number) => (state): ActionResult =>
  state.getIn([...personsListPath, personId, 'updateProfileAction', 'result']);

export const selectRemindLaterActionResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'remindLaterActionResult']);

// TODO delete after class components will be removed
export const makeSelectPersonProfile = createPersonSelectorFactory('profile');
export const makeSelectPersonAdditionalFields = createPersonSelectorFactory('additionalFields');

const selectPersonsProfileDomain = (state, { customerId }: { customerId: number }) => {
  return state.getIn([...personsListPath, customerId]);
};

export const makeSelectPersonNotes = () =>
  createSelector(selectPersonsProfileDomain, profile => (profile ? profile.get('notes') : []));
export const makeSelectPersonNotesMeta = () =>
  createSelector(selectPersonsProfileDomain, profile =>
    profile ? profile.get('notesMeta') : null,
  );
export const makeSelectPersonNotesLoading = () =>
  createSelector(selectPersonsProfileDomain, profile => Boolean(profile?.get('notesLoading')));

export const makeSelectCreatedNote = () =>
  createSelector(selectPersonsProfileDomain, profile =>
    profile ? profile.get('createdNote') : {},
  );
export const selectCreatedNote = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'createdNote']);
export const makeSelectCreateNoteLoading = () =>
  createSelector(selectPersonsProfileDomain, profile => Boolean(profile?.get('addNoteLoading')));
export const selectCreateNoteLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'addNoteLoading']);

export const makeSelectUpdatedNote = () =>
  createSelector(selectPersonsProfileDomain, profile =>
    profile ? profile.get('updatedNote') : {},
  );
export const makeSelectUpdatedNoteLoading = () =>
  createSelector(selectPersonsProfileDomain, profile =>
    Boolean(profile?.get('updatingNoteLoading')),
  );

export const makeSelectDeletedNote = () =>
  createSelector(selectPersonsProfileDomain, profile =>
    profile ? profile.get('deletedNote') : {},
  );
export const makeSelectDeletedNoteLoading = () =>
  createSelector(selectPersonsProfileDomain, profile =>
    Boolean(profile?.get('deletingNoteLoading')),
  );

// Payment methods
export const selectStoredCreditCards = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'cards']);
export const selectPaymentMethodsModalDataLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'paymentMethodsModalDataLoading']);
export const selectPersonSubscriptions = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'subscriptions']);
export const selectUpdatePersonSubscriptionsActionLoading = (personId: number) => state =>
  state.getIn([
    ...personsListPath,
    personId,
    'paymentMethods',
    'updateSubscriptionsAction',
    'isLoading',
  ]);
export const selectUpdatePersonSubscriptionsActionResult = (personId: number) => state =>
  state.getIn([
    ...personsListPath,
    personId,
    'paymentMethods',
    'updateSubscriptionsAction',
    'result',
  ]);
export const selectPersonPaymentMethods = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'list']);
export const selectAddCreditCardResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'addCardResult']);
export const selectAddCreditCardResultLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'addCardResultLoading']);
export const selectDeleteCardResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'deleteCardResult']);
export const selectDeleteCardResultLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentMethods', 'deleteCardResultLoading']);

// Checking
export const selectCheckingSavingsData = (personId: number) => (state): List<IPaymentAccountImt> =>
  state.getIn([...personsListPath, personId, 'checkingSavings']);
export const selectFetchCheckingSavingsDataLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'fetchCheckingSavingsLoading']);
export const selectAddCheckingSavingsDataLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'addCheckingSavingsLoading']);
export const selectAddCheckingSavingsDataActionResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'addCheckingSavingsActionResult']);
export const selectDeleteCheckingSavingsItemLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'deleteCheckingSavingsLoading']);
export const selectDeleteCheckingSavingsItemActionResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'deleteCheckingSavingsActionResult']);

// note codes
export const makeSelectNoteCodes = createSelector(selectPersonsProfileDomain, profile =>
  profile ? profile.get('noteCodes') : {},
);
export const selectNoteCodes = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'noteCodes']);
export const makeSelectNoteCodesIsLoading = createSelector(selectPersonsProfileDomain, profile =>
  Boolean(profile?.get('noteCodesIsLoading')),
);
export const selectNoteCodesLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'noteCodesIsLoading']);

// fallthrough capture
export const selectPersonFallthroughCapture = (personId: number) => (
  state,
): IFallthroughCaptureImt | null => {
  return state.getIn([...personsListPath, personId, 'fallthroughCapture']);
};

export const selectPersonFallthroughCaptureForClass = () =>
  createSelector(selectPersonPanelDomain, state => {
    const st = state || fromJS(personInitialState);

    return st.get('fallthroughCapture');
  });

export const selectPersonFallthroughCaptureActionResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'fallthroughCaptureActionResult']);

export const selectPersonFallthroughCaptureActionLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'fallthroughCaptureActionLoading']);

export const selectPersonCampaigns = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'campaigns']);

export const selectPersonCampaignsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'campaignsLoading']);

export const selectPersonCampaignsHistoryMeta = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'campaignsHistoryMeta']);
export const selectPersonCampaignsHistory = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'campaignsHistory']);
export const selectPersonCampaignsHistoryLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'campaignsHistoryLoading']);

export const selectPersonEmail = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'email']);

export const selectPersonEmailLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'emailLoading']);

export const selectPersonSuggestedProducts = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'suggestedProducts']);
export const selectPersonSuggestedProductsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'suggestedProductsLoading']);

export const selectPayments = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'payments']);
export const selectPaymentsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentsLoading']);
export const selectPaymentsMeta = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentsMeta']);
export const selectRecentPayments = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'recentPayments']);
export const selectRecentPaymentsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'recentPaymentsLoading']);
export const selectPaymentDetails = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentDetails']);
export const selectPaymentDetailsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'paymentDetailsLoading']);

export const selectVoidInvoiceActionResult = (personId: number) => {
  return (state): ActionResult | null => {
    return state.getIn([...personsListPath, personId, 'voidInvoice', 'result']);
  };
};
export const selectVoidInvoiceLoading = (personId: number) => {
  return (state): boolean => {
    return state.getIn([...personsListPath, personId, 'voidInvoice', 'isLoading']);
  };
};

export const makeSelectPersonRecentCalls = createPersonSelectorFactory('recentCalls');
export const makeSelectPersonRecentCallsLoading = createPersonSelectorFactory('recentCallsLoading');

export const selectPersonCalls = (personId: number) => {
  return (state): List<ILeadCallHistoryDtoImt> => {
    return state.getIn([...personsListPath, personId, 'calls']);
  };
};
export const selectPersonCallsMeta = (personId: number) => {
  return (state): IPageMetaImt => {
    return state.getIn([...personsListPath, personId, 'callsMeta']);
  };
};
export const selectPersonCallsLoading = (personId: number) => {
  return (state): boolean => {
    return state.getIn([...personsListPath, personId, 'callsLoading']);
  };
};

export const selectPersonCallDetailsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'isCallLoading']);

export const selectPersonCallDetails = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'call']);
export const makeSelectPersonUpdateCallAction = createPersonSelectorFactory('createCallAction');

export const selectPersonCreateEmailActionResult = (personId: number) => {
  return state => state.getIn([...personsListPath, personId, 'createEmailActionResult']);
};

export const makeSelectPersonCreateSmsActionResult = createPersonSelectorFactory(
  'createSmsActionResult',
);

export const makeSelectPersonRecentEmails = createPersonSelectorFactory('recentEmails');
export const makeSelectPersonRecentEmailsLoading = createPersonSelectorFactory(
  'recentEmailsLoading',
);

export const selectPersonEmailsMeta = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'emailsMeta']);
export const selectPersonEmails = (personId: number) => (state): List<ILeadEmailHistoryDtoImt> =>
  state.getIn([...personsListPath, personId, 'emails']);
export const selectPersonEmailsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'emailsLoading']);

export const makeSelectPersonSMS = createPersonSelectorFactory('sms');
export const makeSelectPersonSMSLoading = createPersonSelectorFactory('smsLoading');

export const selectPersonSMSDetails = (personId: number) => (state: AppStoreTypeImt): ISMSImt =>
  state.getIn([...personsListPath, personId, 'smsDetails']);
export const selectPersonSMSDetailsLoading = (personId: number) => (
  state: AppStoreTypeImt,
): boolean => state.getIn([...personsListPath, personId, 'smsDetailsLoading']);

export const selectPersonSMSHistoryMeta = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'smsHistoryMeta']);
export const selectPersonSMSHistory = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'smsHistory']);
export const selectPersonSMSHistoryLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'smsHistoryLoading']);

export const selectPersonReminders = (personId: number) => (
  state: AppStoreTypeImt,
): List<ReminderHistoryDtoImt> => {
  return state.getIn([...personsListPath, personId, 'reminders']);
};
export const selectPersonRemindersMeta = (personId: number) => (
  state: AppStoreTypeImt,
): IPageMetaImt => {
  return state.getIn([...personsListPath, personId, 'remindersMeta']);
};
export const selectPersonRemindersLoading = (personId: number) => (
  state: AppStoreTypeImt,
): boolean => {
  return state.getIn([...personsListPath, personId, 'remindersLoading']);
};

export const selectPersonReminder = (personId: number) => (
  state: AppStoreTypeImt,
): IReminderDtoImt => {
  return state.getIn([...personsListPath, personId, 'reminder']);
};
export const selectPersonReminderLoading = (personId: number) => (
  state: AppStoreTypeImt,
): boolean => {
  return state.getIn([...personsListPath, personId, 'reminderLoading']);
};

export const makeSelectPersonRecentReminders = createPersonSelectorFactory('recentReminders');
export const makeSelectPersonRecentRemindersLoading = createPersonSelectorFactory(
  'remindersLoading',
);

export const makeSelectAddPersonReminderActionResult = createPersonSelectorFactory(
  'addReminderAction',
);

// Modification history

export const selectModificationHistory = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'modificationHistory']);
export const selectModificationHistoryLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'modificationHistoryLoading']);
export const selectModificationHistoryMeta = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'modificationHistoryMeta']);
export const selectRecentModificationHistory = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'recentModificationHistory']);
export const selectRecentModificationHistoryLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'recentModificationHistoryLoading']);
export const selectModificationHistoryItem = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'modificationHistoryItem']);
export const selectModificationHistoryItemLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'modificationHistoryItemLoading']);

// CheckIns history

export const selectRecentCheckInsHistoryList = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'recentCheckInsHistory']);
export const selectRecentCheckInsHistoryLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'recentCheckInsHistoryLoading']);
export const selectDocumentsHistoryList = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'documentsHistory']);
export const selectDocumentsHistoryLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'isDocumentsHistoryLoading']);
export const selectDocumentsHistoryMeta = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'documentsHistoryMeta']);
export const selectCheckInsHistoryList = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'checkInsHistory']);
export const selectCheckInsHistoryMeta = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'checkInsHistoryMeta']);
export const selectCheckInsHistoryLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'checkInsHistoryLoading']);

export const selectUpcomingBillings = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'upcomingBillings']);
export const selectUpcomingBillingsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'upcomingBillingsLoading']);
export const selectUpdateBillingScheduleAmountActionLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'updateBillingScheduleAmountAction', 'isLoading']);
export const selectUpdateBillingScheduleAmountActionResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'updateBillingScheduleAmountAction', 'result']);

export const selectPastDueBillings = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'pastDueBillings']);

export const selectPastDueBillingsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'pastDueBillingsLoading']);

const selectProfilesDomain = state => {
  return state.getIn(['persons', 'profiles']);
};

export const selectUpdateImage = (id: number) => {
  return createSelector(
    selectProfilesDomain,
    (state): IStoredFileSimpleDtoImt => state.getIn([id, 'profile', 'image']),
  );
};
export const selectUpdateImageLoading = (id: number) => {
  return createSelector(selectProfilesDomain, state =>
    state.getIn([id, 'profile', 'imageLoading']),
  );
};

// sales info
export const selectPersonSalesInfo = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'salesInfo']);

export const selectPersonSalesInfoLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'salesInfoLoading']);

export const selectPersonSalesInfoUpdateLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'salesInfoActionStatus', 'isLoading']);

export const selectPersonSalesInfoUpdateResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'salesInfoActionStatus', 'result']);

// Documents

export const selectPersonDocuments = (personId: number) => (state): IPersonDocumentsImt | null =>
  state.getIn([...personsListPath, personId, 'documents']);

export const selectPersonDocumentsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'isDocumentsLoading']);

export const selectPersonDocumentHTMLLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'isDocumentHTMLLoading']);

export const selectPersonDocumentActionResult = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'documentActionStatus', 'result']);

export const selectPersonDocumentActionLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'documentActionStatus', 'isLoading']);

// Alerts

export const selectPersonAlertsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'alertsLoading']);

export const selectPersonAlerts = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'alerts']);

export const selectIsUpdateRewardsLoading = (personId: number) => state =>
  state.getIn([...personsListPath, personId, 'isUpdateRewardsLoading']);

// Referrals

export const selectPersonBalanceHistory = (personId: number) => state => {
  return state.getIn([...personsListPath, personId, 'balanceHistory']);
};

export const selectPersonBalanceHistoryLoading = (personId: number) => state => {
  return state.getIn([...personsListPath, personId, 'balanceHistoryLoading']);
};

export const selectPersonRewardsHistory = (personId: number) => state => {
  return state.getIn([...personsListPath, personId, 'rewardsHistory']);
};

export const selectPersonRewardsHistoryLoading = (personId: number) => state => {
  return state.getIn([...personsListPath, personId, 'rewardsHistoryLoading']);
};
